import { Controller } from "@hotwired/stimulus"
import { classifyQuality, QUALITY_GOOD, QUALITY_FAIR, QUALITY_POOR, QUALITY_VERY_POOR } from "../../pwa_network_quality"

// Shared network diagnostics page controller for PWA apps.
//
// Provides: status detection, ping test with dot visualisation, latency/packet-loss,
// connection type/speed, service worker status, and copy diagnostics.
//
// Stimulus values:
//   prefix   -- localStorage/event namespace (e.g. "field", "chat")
//   pingUrl  -- server endpoint to ping (e.g. "/field/ping", "/chat/ping")
//
// Subclasses can override:
//   _loadCacheStats()    -- populate offline cache section (app-specific)
//   _extraDiagnostics()  -- append lines to copy-diagnostics output
//   _log(message)        -- logging hook (e.g. field_logger)

const PING_COUNT   = 5
const PING_TIMEOUT = 8000

export default class extends Controller {
  static targets = [
    "statusDot", "statusLabel",
    "pingResults", "pingDots", "avgLatency", "packetLoss", "qualityLabel",
    "connectionType", "downlink", "networkInfo",
    "copyBtn", "runBtn", "offlineBanner",
    "swStatus"
  ]

  static values = {
    prefix:  String,
    pingUrl: String
  }

  connect() {
    this._running = false
    this._lastResults = []
    this._updateStatus()
    this._loadCacheStats()
    this._loadNetworkInfo()
    this._checkServiceWorker()
    this._checkManualOfflineMode()

    this._onlineHandler  = () => this._updateStatus()
    this._offlineHandler = () => this._updateStatus()
    window.addEventListener(`${this.prefixValue}:online`,  this._onlineHandler)
    window.addEventListener(`${this.prefixValue}:offline`, this._offlineHandler)
  }

  disconnect() {
    window.removeEventListener(`${this.prefixValue}:online`,  this._onlineHandler)
    window.removeEventListener(`${this.prefixValue}:offline`, this._offlineHandler)
  }

  async runTest() {
    if (this._running) return
    this._running = true
    this._lastResults = []

    if (this.hasRunBtnTarget) {
      this.runBtnTarget.disabled = true
      this.runBtnTarget.textContent = "Testing..."
    }

    // Reset dots to pending state
    if (this.hasPingDotsTarget) {
      this.pingDotsTarget.innerHTML = Array(PING_COUNT).fill(
        `<span class="w-3 h-3 rounded-full bg-slate-200 inline-block"></span>`
      ).join("")
    }

    if (this.hasPingResultsTarget) {
      this.pingResultsTarget.classList.remove("hidden")
    }

    const dots = this.hasPingDotsTarget
      ? this.pingDotsTarget.querySelectorAll("span")
      : []

    for (let i = 0; i < PING_COUNT; i++) {
      const result = await this._singlePing()
      this._lastResults.push(result)

      if (dots[i]) {
        if (result.ok) {
          dots[i].className = "w-3 h-3 rounded-full bg-emerald-400 inline-block"
          dots[i].title = `${result.ms}ms`
        } else {
          dots[i].className = "w-3 h-3 rounded-full bg-red-400 inline-block"
          dots[i].title = "Failed"
        }
      }

      if (i < PING_COUNT - 1) await this._sleep(300)
    }

    this._renderResults()
    this._running = false

    if (this.hasRunBtnTarget) {
      this.runBtnTarget.disabled = false
      this.runBtnTarget.textContent = "Run again"
    }
  }

  copyDiagnostics() {
    const text = this._buildDiagnosticText()
    navigator.clipboard?.writeText(text).then(() => {
      if (this.hasCopyBtnTarget) {
        const orig = this.copyBtnTarget.textContent
        this.copyBtnTarget.textContent = "Copied!"
        setTimeout(() => { this.copyBtnTarget.textContent = orig }, 2000)
      }
    })
  }

  // -- override in subclasses --

  _loadCacheStats() {
    // Override to populate app-specific offline cache stats
  }

  _extraDiagnostics() {
    // Override to return additional lines for copy diagnostics
    return []
  }

  _log(_message) {
    // Override for logging (e.g. field_logger)
  }

  // -- private --

  _checkManualOfflineMode() {
    if (localStorage.getItem(`${this.prefixValue}:offline_mode`) === "manual" && this.hasOfflineBannerTarget) {
      this.offlineBannerTarget.classList.remove("hidden")
    }
  }

  _updateStatus() {
    const online = localStorage.getItem(`${this.prefixValue}:online`) !== "false"

    if (this.hasStatusDotTarget) {
      this.statusDotTarget.className = online
        ? "w-3 h-3 rounded-full bg-emerald-400 shrink-0"
        : "w-3 h-3 rounded-full bg-amber-400 shrink-0"
    }
    if (this.hasStatusLabelTarget) {
      this.statusLabelTarget.textContent = online ? "Online" : "Offline"
      this.statusLabelTarget.className   = online
        ? "text-sm font-semibold text-emerald-700"
        : "text-sm font-semibold text-amber-700"
    }
  }

  _loadNetworkInfo() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (!conn) return

    if (this.hasNetworkInfoTarget) this.networkInfoTarget.classList.remove("hidden")

    if (this.hasConnectionTypeTarget) {
      this.connectionTypeTarget.textContent = conn.effectiveType?.toUpperCase() ?? "Unknown"
    }
    if (this.hasDownlinkTarget) {
      this.downlinkTarget.textContent = conn.downlink != null
        ? `${conn.downlink} Mbps`
        : "Unknown"
    }
  }

  async _checkServiceWorker() {
    if (!this.hasSwStatusTarget) return
    if (!("serviceWorker" in navigator)) {
      this.swStatusTarget.textContent = "Not supported"
      return
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (reg) {
        const state = reg.active ? "Active" : reg.installing ? "Installing" : reg.waiting ? "Waiting" : "Registered"
        this.swStatusTarget.textContent = state
        this.swStatusTarget.className = "text-sm font-medium text-emerald-600"
      } else {
        this.swStatusTarget.textContent = "Not registered"
        this.swStatusTarget.className = "text-sm font-medium text-amber-600"
      }
    } catch {
      this.swStatusTarget.textContent = "Error"
    }
  }

  async _singlePing() {
    const sep   = this.pingUrlValue.includes("?") ? "&" : "?"
    const url   = `${this.pingUrlValue}${sep}t=${Date.now()}`
    const start = performance.now()
    try {
      const res = await fetch(url, {
        method: "GET",
        cache:  "no-store",
        signal: AbortSignal.timeout(PING_TIMEOUT)
      })
      const ms = Math.round(performance.now() - start)
      return { ok: res.ok, ms }
    } catch {
      return { ok: false, ms: null }
    }
  }

  _renderResults() {
    const successful = this._lastResults.filter(r => r.ok)
    const failed     = this._lastResults.length - successful.length
    const latencies  = successful.map(r => r.ms)
    const avg        = latencies.length
      ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length)
      : null
    const loss = Math.round((failed / this._lastResults.length) * 100)

    if (this.hasAvgLatencyTarget) {
      this.avgLatencyTarget.textContent = avg != null ? `${avg}ms avg` : "No response"
    }
    if (this.hasPacketLossTarget) {
      this.packetLossTarget.textContent = `${loss}% loss`
      this.packetLossTarget.className = loss > 0
        ? "text-sm font-medium text-amber-600"
        : "text-sm font-medium text-slate-400"
    }
    if (this.hasQualityLabelTarget) {
      const { label, className } = this._quality(avg, loss)
      this.qualityLabelTarget.textContent = label
      this.qualityLabelTarget.className   = className
    }
  }

  // Band thresholds live in pwa_network_quality.js -- the SAME classifier
  // field_network_health.js uses to drive the three-level connectivity model
  // (ADR-0110). Only the label/colour presentation is local to this page.
  _quality(avgMs, lossPercent) {
    const presentation = {
      [QUALITY_VERY_POOR]: { label: "Very poor", className: "text-sm font-semibold text-red-600" },
      [QUALITY_POOR]:      { label: "Poor",      className: "text-sm font-semibold text-amber-600" },
      [QUALITY_FAIR]:      { label: "Fair",      className: "text-sm font-semibold text-yellow-600" },
      [QUALITY_GOOD]:      { label: "Good",      className: "text-sm font-semibold text-emerald-600" }
    }
    return presentation[classifyQuality(avgMs, lossPercent)]
  }

  _buildDiagnosticText() {
    const online   = localStorage.getItem(`${this.prefixValue}:online`) !== "false"
    const conn     = navigator.connection

    const pings = this._lastResults.length
      ? this._lastResults.map(r => r.ok ? `${r.ms}ms` : "FAIL").join(", ")
      : "No test run"

    const lines = [
      `WIN Diagnostics -- ${new Date().toLocaleString()}`,
      `App: ${this.prefixValue}`,
      `Status: ${online ? "Online" : "Offline"}`,
      `Ping results: ${pings}`,
    ]

    if (conn) {
      lines.push(`Connection type: ${conn.effectiveType ?? "unknown"}`)
      lines.push(`Downlink: ${conn.downlink != null ? conn.downlink + " Mbps" : "unknown"}`)
    }

    lines.push(...this._extraDiagnostics())

    return lines.join("\n")
  }

  _formatBytes(bytes) {
    if (bytes < 1024)        return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}
